import React, { useState, useEffect } from 'react';
import {
  Add,
  WifiOff,
  Sync,
  Search,
  DateRange,
  FilterList,
  Event,
  CheckCircle,
  MoreVert,
  Vaccines,
  ChildCare,
  FirstPage,
  ChevronLeft,
  ChevronRight,
  LastPage,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAllImmunization } from '../services/authService';
import { showSuccess, showError } from '../utils/alert';
import Loader from '../components/Loader';
import { useSelector } from 'react-redux';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ashaapi.bluai.ai';

const Immunization = () => {
  const navigate = useNavigate();

  // State for data
  const selectedVillageId = useSelector((state) => state.village.selectedVillageId);
  const [dueImmunizations, setDueImmunizations] = useState([]);
  const [vaccineCoverage, setVaccineCoverage] = useState([]);
  const [vaccineTypes] = useState([
    { value: 'bcg', label: 'BCG' },
    { value: 'opv', label: 'OPV' },
    { value: 'pentavalent', label: 'Pentavalent' },
    { value: 'rotavirus', label: 'Rotavirus' },
    { value: 'pcv', label: 'PCV' },
    { value: 'ipv', label: 'IPV' },
    { value: 'mmr', label: 'MMR' },
    { value: 'je', label: 'JE' },
    { value: 'dpt', label: 'DPT' },
    { value: 'tt', label: 'TT' },
  ]);
  const [statuses] = useState([
    { value: 'due', label: 'Due' },
    { value: 'completed', label: 'Completed' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'scheduled', label: 'Scheduled' },
  ]);

  // State for filters and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVaccine, setSelectedVaccine] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateFilter, setDateFilter] = useState('Next 30 days');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 10;

  // State for UI
  const [isLoading, setIsLoading] = useState(false);
  const [vaccineCoverageLoading, setVaccineCoverageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Handle offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch data on mount and when filters/pagination change
  useEffect(() => {
    fetchDueImmunizations(currentPage);
    fetchVaccineCoverage();
  }, [currentPage, searchQuery, selectedVaccine, selectedStatus, dateFilter, selectedVillageId]);

  // Fetch due immunizations
  const fetchDueImmunizations = async (page = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getAllImmunization(page, perPage, {
        search: searchQuery,
        vaccine: selectedVaccine !== 'all' ? selectedVaccine : undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        dateFilter: dateFilter,
      });

      if (response.status === true) {
        const records = response.data?.data || [];

        // Transform backend data to frontend format
        const formattedRecords = records.map((item) => {
          const nextDueDate = item.next_due_date ? new Date(item.next_due_date) : null;
          const today = new Date();
          let status = 'Completed';
          let dueStatus = 'completed';

          if (nextDueDate) {
            if (nextDueDate < today) {
              status = 'Overdue';
              dueStatus = 'overdue';
            } else if (
              nextDueDate <= new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
            ) {
              status = 'Due';
              dueStatus = 'due';
            } else {
              status = 'Scheduled';
              dueStatus = 'scheduled';
            }
          }

          return {
            id: item.record_id,
            childName: item.beneficiary_name || 'Unknown',
            vaccineName: item.vaccine_name ? item.vaccine_name.toUpperCase() : 'N/A',
            dueDateText: nextDueDate ? nextDueDate.toLocaleDateString() : 'N/A',
            dueStatus,
            status,
            age: item.beneficiary_age || 'N/A',
            gender: item.beneficiary_gender || 'N/A',
            location: item.beneficiary_location || 'N/A',
            ageGroup: item.vaccine_type || 'N/A',
          };
        });

        setDueImmunizations(formattedRecords);
        setCurrentPage(response.data?.meta?.current_page || 1);
        setTotalPages(response.data?.meta?.last_page || 1);
        setTotalRecords(response.data?.meta?.total || formattedRecords.length);
      } else {
        setDueImmunizations([]);
       
      }
    } catch (err) {
      console.error('Error fetching immunizations:', err);
      setDueImmunizations([]);
      setError('Failed to fetch immunizations. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch vaccine coverage
  const fetchVaccineCoverage = async () => {
    setVaccineCoverageLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/asha/vaccines/coverage`);
      setVaccineCoverage(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch vaccine coverage:', err);  
      setVaccineCoverage([]);
    } finally {
      setVaccineCoverageLoading(false);
    }
  };

  // Handle filter changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleVaccineChange = (e) => {
    setSelectedVaccine(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (e) => {
    setDateFilter(e.target.value);
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle adding new immunization
  const handleAddImmunization = () => {
    navigate('/immunization-add');
  };

  // Handle action button clicks
  const handleAction = async (id, action) => {
    try {
      await axios.post(`${API_BASE_URL}/api/asha/immunizations/${id}/action`, { action });
      showSuccess(`Immunization ${action} successful`);
      fetchDueImmunizations(currentPage);
    } catch (err) {
      console.error('Error performing action:', err);
      showError(`Failed to perform ${action}`);
    }
  };

  if (isLoading || vaccineCoverageLoading) return <Loader />;

  return (
    <>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">Immunization</div>
        <button className="add-button" onClick={handleAddImmunization}>
          <Add className="icon" />
          New Immunization
        </button>
      </div>

    


      {/* Error Message */}
      {error && <div className="error-message">{error}</div>}

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="search-field">
          <Search className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by child name..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        <select
          className="filter-dropdown"
          value={selectedVaccine}
          onChange={handleVaccineChange}
        >
          <option value="all">All Vaccines</option>
          {vaccineTypes.map((vaccine) => (
            <option key={vaccine.value} value={vaccine.value}>
              {vaccine.label}
            </option>
          ))}
        </select>
        <select
          className="filter-dropdown"
          value={selectedStatus}
          onChange={handleStatusChange}
        >
          <option value="all">All Statuses</option>
          {statuses.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
        <select
          className="filter-dropdown"
          value={dateFilter}
          onChange={handleDateFilterChange}
        >
          <option value="Next 7 days">Next 7 days</option>
          <option value="Next 30 days">Next 30 days</option>
          <option value="Last 30 days">Last 30 days</option>
        </select>
        <button className="filter-button">
          <FilterList className="icon" />
          Filters
        </button>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        <div className="tab active">
          Due Immunizations
          <span className="tab-count">{totalRecords}</span>
        </div>
        <div className="tab">
          Vaccine Coverage
          <span className="tab-count">{vaccineCoverage.length}</span>
        </div>
        <div className="tab">
          All Records
          <span className="tab-count">{totalRecords}</span> {/* TODO: Fetch dynamically */}
        </div>
        <div className="tab">
          Outreach Sessions
          <span className="tab-count">0</span> {/* TODO: Fetch dynamically */}
        </div>
      </div>

      {/* Due Immunization List */}
      <div className="due-list">
        <div className="due-list-header">
          <div>Beneficiary</div>
          <div>Vaccine</div>
          <div>Due Date</div>
          <div>Status</div>
          <div>Actions</div>
        </div>
        {dueImmunizations.length === 0 ? (
          <div className="no-data">No immunizations found.</div>
        ) : (
          dueImmunizations.map((item) => (
            <div className="due-list-item" key={item.id}>
              <div className="beneficiary-info">
                <div className="beneficiary-avatar child-avatar">
                  <ChildCare />
                </div>
                <div className="beneficiary-details">
                  <div className="beneficiary-name">{item.childName}</div>
                  <div className="beneficiary-meta">
                    {item.age} • {item.gender} • {item.location}
                  </div>
                </div>
              </div>
              <div className="vaccine-info">
                <Vaccines className="vaccine-icon" />
                <div>
                  <div className="vaccine-name">{item.vaccineName}</div>
                  <div className="beneficiary-meta">{item.ageGroup}</div>
                </div>
              </div>
              <div className={`due-date ${item.dueStatus.toLowerCase()}`}>
                {item.dueDateText}
              </div>
              <div className={`status-chip status-${item.status.toLowerCase()}`}>
                {item.status}
              </div>
              <div className="action-buttons">
                <div
                  className="action-button-small"
                  onClick={() => handleAction(item.id, 'complete')}
                >
                  <CheckCircle />
                </div>
                <div
                  className="action-button-small"
                  onClick={() => handleAction(item.id, 'schedule')}
                >
                  <Event />
                </div>
                <div className="action-button-small">
                  <MoreVert />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Vaccine Coverage Cards */}
      <div className="card-grid">
        {vaccineCoverage.length === 0 ? (
          <div className="no-data">No vaccine coverage data available.</div>
        ) : (
          vaccineCoverage.map((vaccine) => (
            <div className="vaccine-card" key={vaccine.id}>
              <div className="vaccine-card-header">
                <div>{vaccine.name || 'Unknown'}</div>
                <div className="vaccine-count">{vaccine.target || 0}</div>
              </div>
              <div className="vaccine-card-content">
                <div className="age-group">
                  <ChildCare className="age-group-icon" />
                  {vaccine.ageGroup || 'N/A'}
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-value"
                    style={{ width: `${vaccine.coverage || 0}%` }}
                  ></div>
                </div>
                <div className="vaccine-stats">
                  <div>
                    Coverage: <span className="stats-value">{vaccine.coverage || 0}%</span>
                  </div>
                  <div>
                    Target: <span className="stats-value">{vaccine.target || 0}</span>
                  </div>
                  <div>
                    Given: <span className="stats-value">{vaccine.given || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
          <FirstPage />
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft />
        </button>
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            className={`pagination-button ${currentPage === i + 1 ? 'active' : ''}`}
            onClick={() => handlePageChange(i + 1)}
          >
            {i + 1}
          </button>
        ))}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight />
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <LastPage />
        </button>
        <div className="pagination-text">
          Page {currentPage} of {totalPages}
        </div>
      </div>
    </>
  );
};

export default Immunization;  